import { APP_ROUTES } from "@/shared/lib/routes/routes/app-routes";

/**
 * Pure helpers extracted from proxy.ts to keep `proxy()` and `buildCsp()`
 * at CC ≤ 4. Every export here is intentionally branch-light.
 *
 * Two concerns:
 *   1. CSP builder utilities — pure data manipulation around connect-src
 *      derivation. The header itself is built from a fixed directive list.
 *   2. Auth-protected path classification + safe-redirect parsing.
 */
// Browser-only request shape for resolvePostLoginTarget.
export interface AuthRedirectRequest {
	searchParams: URLSearchParams;
	url: string;
}

// ---------------------------------------------------------------------------
// CSP helpers
// ---------------------------------------------------------------------------

export interface DerivedOrigins {
	backendOrigin: string;
}

const EMPTY_ORIGINS: DerivedOrigins = { backendOrigin: "" };

function tryParseUrl(value: string): URL | null {
	try {
		return new URL(value);
	} catch {
		return null;
	}
}

/**
 * Try to derive backendOrigin from a `VITE_BACKEND_URL`-style
 * value. Returns empty strings when input is empty / invalid. CC=3.
 */
export function deriveOriginsFromBackendUrl(
	backendUrl: string | undefined,
): DerivedOrigins {
	if (!backendUrl) {
		return EMPTY_ORIGINS;
	}
	const u = tryParseUrl(backendUrl);
	if (!u) {
		return EMPTY_ORIGINS;
	}
	return { backendOrigin: u.origin };
}

function parseHostHeaderParts(
	hostHeader: string,
): { hostname: string; portStr: string } | null {
	const [hostname, portStr] = hostHeader.split(":");
	if (!(hostname && portStr)) {
		return null;
	}
	return { hostname, portStr };
}

/**
 * In dev (with --hostname 0.0.0.0 etc.) the proxy infers the backend origin by
 * adding 1000 to the page's port. Extract that from the inbound `Host` header.
 * Returns empty strings when the header is missing or unparseable. CC=3.
 */
export function deriveOriginsFromHostHeader(
	hostHeader: string | null,
): DerivedOrigins {
	if (!hostHeader) {
		return EMPTY_ORIGINS;
	}
	const parts = parseHostHeaderParts(hostHeader);
	if (!parts) {
		return EMPTY_ORIGINS;
	}
	const backendPort = String(Number(parts.portStr) + 1000);
	return { backendOrigin: `http://${parts.hostname}:${backendPort}` };
}

/**
 * In dev, add localhost entries when the resolved backend host isn't already
 * localhost. Returns the new entries to append. CC=3.
 */
export function devLocalhostFallbackEntries(
	isDev: boolean,
	backendOrigin: string,
): string[] {
	if (!(isDev && backendOrigin)) {
		return [];
	}
	const bo = tryParseUrl(backendOrigin);
	if (!bo || bo.hostname === "localhost") {
		return [];
	}
	return [`http://localhost:${bo.port}`];
}

const STATIC_CONNECT_SOURCES = [
	// emoji-picker-element CDN (jsDelivr) — see proxy.ts JSDoc.
	"https://cdn.jsdelivr.net",
	// tldraw assets / translations CDN.
	"https://cdn.tldraw.com",
	// LocationPickerDialog map tiles/vector data + Places autocomplete.
	"https://maps.googleapis.com",
	"https://maps.gstatic.com",
	"https://places.googleapis.com",
] as const;

function originAsEntries(origins: DerivedOrigins): string[] {
	return [origins.backendOrigin].filter(Boolean);
}

/** Build the connect-src list from derived origin pieces. CC=2. */
export function buildConnectSrcList(opts: {
	isDev: boolean;
	envOrigins: DerivedOrigins;
	hostOrigins: DerivedOrigins;
}): string[] {
	const merged: DerivedOrigins = opts.envOrigins.backendOrigin
		? opts.envOrigins
		: opts.hostOrigins;
	return [
		"'self'",
		...originAsEntries(merged),
		...devLocalhostFallbackEntries(opts.isDev, merged.backendOrigin),
		...STATIC_CONNECT_SOURCES,
	];
}

/** Choose the script-src directive based on env. CC=1. */
export function scriptSrcForEnv(isDev: boolean): string {
	// Google Maps JS API bootstraps additional module scripts from
	// maps.googleapis.com / maps.gstatic.com.
	const maps = "https://maps.googleapis.com https://maps.gstatic.com";
	return isDev
		? `script-src 'self' 'unsafe-inline' 'unsafe-eval' ${maps}`
		: `script-src 'self' 'unsafe-inline' ${maps}`;
}

const STATIC_CSP_DIRECTIVES = [
	"default-src 'self'",
	"base-uri 'self'",
	"form-action 'self'",
	"frame-ancestors 'none'",
	"object-src 'none'",
	"script-src-attr 'none'",
	"style-src 'self' 'unsafe-inline' https://cdn.tldraw.com https://fonts.googleapis.com",
	"style-src-elem 'self' 'unsafe-inline' https://cdn.tldraw.com https://fonts.googleapis.com",
	"img-src 'self' data: blob: https://flagcdn.com https://img.youtube.com https://i.ytimg.com https://images.unsplash.com https://cdn.tldraw.com https://*.s3.amazonaws.com https://i.pravatar.cc https://maps.googleapis.com https://maps.gstatic.com https://fonts.gstatic.com https://streetviewpixels-pa.googleapis.com https://lh3.googleusercontent.com https://lh4.googleusercontent.com https://lh5.googleusercontent.com https://khms0.googleapis.com https://khms1.googleapis.com https://cbks0.googleapis.com https://cbks1.googleapis.com",
	"font-src 'self' data: blob: https://fonts.gstatic.com https://cdn.tldraw.com",
	"frame-src https://www.youtube.com https://www.youtube-nocookie.com https://offline.tawkit.net",
	"worker-src 'self' blob:",
] as const;

/**
 * Assemble the final CSP header value. Pure function — takes pre-derived
 * pieces and joins them. CC=1.
 */
export function assembleCspHeader(opts: {
	isDev: boolean;
	connectSources: string[];
}): string {
	return [
		...STATIC_CSP_DIRECTIVES,
		scriptSrcForEnv(opts.isDev),
		`connect-src ${opts.connectSources.join(" ")}`,
	].join("; ");
}

// ---------------------------------------------------------------------------
// Routing helpers (auth gate, redirect targets, route classification)
// ---------------------------------------------------------------------------

const PROTECTED_PREFIXES = [
	"/chat",
	"/dashboard",
	"/documents",
	"/config",
	"/superuser",
	"/playground",
] as const;

/** True iff `pathname` is one of the auth-protected app routes. CC=2. */
export function isProtectedPath(pathname: string): boolean {
	return PROTECTED_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * True iff `value` is a safe in-app redirect target (relative, no scheme).
 * Defends against open-redirect via `?redirectTo=`. CC=3.
 */
export function isSafeRedirectTarget(value: string | null): boolean {
	if (!value) {
		return false;
	}
	if (!value.startsWith("/") || value.startsWith("//")) {
		return false;
	}
	return !value.includes("://");
}

const MIN_SESSION_TOKEN_LENGTH = 24;

/** True iff `token` (from a cookie) is plausibly a real Better Auth token. CC=2. */
export function isPlausibleSessionToken(
	token: string | null | undefined,
): boolean {
	const trimmed = token?.trim();
	return (
		typeof trimmed === "string" && trimmed.length >= MIN_SESSION_TOKEN_LENGTH
	);
}

/** True iff `pathname` is /login or a subpath. CC=1. */
export function isLoginPath(pathname: string): boolean {
	return pathname === "/login" || pathname.startsWith("/login/");
}

/** True iff `pathname` is /monitoring or a subpath (Sentry tunnel bypass). CC=1. */
export function isMonitoringPath(pathname: string): boolean {
	return pathname === "/monitoring" || pathname.startsWith("/monitoring/");
}

/** True iff `pathname` is a /themes/*.css rewrite candidate. CC=1. */
export function isThemeCssPath(pathname: string): boolean {
	return pathname.startsWith("/themes/") && pathname.endsWith(".css");
}

/** Extract theme name from a `/themes/<name>.css` pathname. CC=1. */
export function extractThemeName(pathname: string): string {
	return pathname.replace("/themes/", "").replace(".css", "");
}

/**
 * Resolve the desired post-login redirect target for a user landing on /login
 * with a valid session. Returns either an absolute URL or `null` when the
 * loopback "go to /home" fallback should be used by the caller. CC=3.
 */
export function resolvePostLoginTarget(request: AuthRedirectRequest): {
	url: URL;
	isLoopback: boolean;
} {
	const redirectTo = request.searchParams.get("redirectTo");
	if (redirectTo && isSafeRedirectTarget(redirectTo)) {
		const target = new URL(redirectTo, request.url);
		const isLoopback =
			target.pathname === "/login" || target.pathname.startsWith("/login/");
		return {
			url: isLoopback ? new URL(APP_ROUTES.home, request.url) : target,
			isLoopback,
		};
	}
	return { url: new URL(APP_ROUTES.home, request.url), isLoopback: false };
}
